import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Gavel, Loader2, Clock, MapPin, Flame, Package, Trophy, RefreshCw, ChevronLeft, EyeOff, Eye, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const eur = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);

const formatRemain = (sec) => {
  if (sec <= 0) return "abgelaufen";
  const h = Math.floor(sec / 3600); const m = Math.floor((sec % 3600) / 60); const s = sec % 60;
  if (h > 0) return `${h} h ${m} min`;
  if (m > 0) return `${m} min ${s} s`;
  return `${s} s`;
};

const PKG_COLOR = { guenstig: "#60A5FA", komfort: "#34D399", premium: "#F59E0B" };

export function LeadAuctionMarket() {
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");
  const [tick, setTick] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const status = tab === "open" ? "open" : (tab === "won" ? "closed_awarded" : "closed_awarded");
      const mineOnly = tab === "mine";
      const { data } = await api.get(`/lead-auctions?status=${status}&mine_only=${mineOnly}`);
      setAuctions(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab]);
  useEffect(() => { const t = setInterval(() => setTick(x => x+1), 1000); return () => clearInterval(t); }, []);

  const decorated = useMemo(() => auctions.map(a => ({
    ...a, _remain: Math.max(0, a.remaining_seconds - (tick % 60))
  })), [auctions, tick]);

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="lead-auction-market">
      <AppHeader title="Lead-Auktionen" back="/dashboard" />
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-3">
            <Gavel className="text-[#00E575]" size={22}/>
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">Lead-Auktionen</h1>
              <p className="text-[12px] text-zinc-400 inline-flex items-center gap-1 font-medium"><EyeOff size={11}/> Verdeckte Auktion · 1 Gebot · Höchstpreis gewinnt</p>
            </div>
          </div>
          <button onClick={load} className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-800 text-[12px] inline-flex items-center gap-1.5 hover:border-[#00E575]/40 font-medium"><RefreshCw size={13}/></button>
        </div>
        <div className="flex gap-1">
          {[{k:"open",l:"Offen"},{k:"mine",l:"Meine Gebote"},{k:"won",l:"Gewonnen/Verloren"}].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`auction-tab-${t.k}`}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-bold border ${tab === t.k ? "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40" : "bg-[#18181B] text-zinc-400 border-zinc-800 hover:border-zinc-600"}`}>
              {t.l}
            </button>
          ))}
        </div>

        {loading && <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 flex justify-center"><Loader2 className="animate-spin text-zinc-400"/></div>}

        {!loading && decorated.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">Keine Auktionen in dieser Ansicht.</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {decorated.map(a => {
            const lp = a.lead_preview || {};
            const pkgColor = PKG_COLOR[lp.package] || "#71717A";
            return (
              <button key={a.id} onClick={() => navigate(`/lead-auctions/${a.id}`)} data-testid={`auction-card-${a.id}`}
                className="text-left bg-[#18181B] border border-zinc-800 rounded-2xl p-4 hover:border-[#00E575]/40 transition-colors relative">
                {a.in_backup_pool && <span className="absolute top-2 right-2 text-[12px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded">🔥 Backup-Pool</span>}
                <div className="flex items-start gap-2">
                  <span className="text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: `${pkgColor}20`, color: pkgColor }}>{lp.package || "—"}</span>
                  {a.my_bid && <span className="text-[12px] font-bold uppercase tracking-wider bg-[#00E575]/15 text-[#00E575] px-2 py-0.5 rounded">Geboten {eur(a.my_bid.amount_eur)}</span>}
                </div>
                <div className="mt-2 text-[12px] text-zinc-300 space-y-0.5 font-medium">
                  <div className="inline-flex items-center gap-1"><MapPin size={11}/> {lp.zip} {lp.city}</div>
                  <div className="inline-flex items-center gap-1"><Flame size={11}/> {lp.current_heating || "—"} · {lp.living_area_sqm || "?"} m²</div>
                  <div className="inline-flex items-center gap-1"><Package size={11}/> Termin {lp.appointment_type} · {lp.appointment_date}</div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-[12px] text-zinc-400"><Clock size={11}/> {formatRemain(a._remain)}</span>
                  <span className="text-[12px] text-zinc-300 font-medium">min {eur(a.min_bid_eur)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export function LeadAuctionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [amount, setAmount] = useState("");
  const [tick, setTick] = useState(0);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/lead-auctions/${id}`); setA(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"); navigate("/lead-auctions"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);
  useEffect(() => { const t = setInterval(() => setTick(x => x+1), 1000); return () => clearInterval(t); }, []);

  const submit = async () => {
    const n = Number(amount);
    if (!n || n <= 0) return toast.error("Ungültiger Betrag");
    if (n < a.min_bid_eur) return toast.error(`Mindestgebot ${eur(a.min_bid_eur)}`);
    if (!window.confirm(`Gebot ${eur(n)} verbindlich abgeben?\nVerdeckte Auktion — kein Ändern möglich!`)) return;
    setSubmitting(true);
    try {
      await api.post(`/lead-auctions/${id}/bid`, { amount_eur: n });
      toast.success(`Gebot ${eur(n)} platziert`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gebot abgelehnt"); }
    finally { setSubmitting(false); }
  };

  if (loading || !a) return (<div className="min-h-screen bg-[#09090B] flex items-center justify-center"><Loader2 className="animate-spin text-[#00E575]"/></div>);

  const lp = a.lead_preview || {};
  const remain = Math.max(0, a.remaining_seconds - (tick % 60));
  const pkgColor = PKG_COLOR[lp.package] || "#71717A";
  const expired = remain <= 0 || a.status !== "open";

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="lead-auction-detail">
      <AppHeader title="Lead-Auktion" back="/lead-auctions" />
      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: `${pkgColor}20`, color: pkgColor }}>{lp.package || "—"}</span>
          {a.in_backup_pool && <span className="text-[12px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded">🔥 Backup-Pool</span>}
          {a.status === "closed_awarded" && <span className="text-[12px] font-bold uppercase tracking-wider bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded">geschlossen</span>}
          {a.won && <span className="text-[12px] font-bold uppercase tracking-wider bg-[#00E575]/15 text-[#00E575] px-2 py-0.5 rounded inline-flex items-center gap-1"><Trophy size={11}/>Gewonnen</span>}
        </div>

        {/* Lead-Vorschau (anonymisiert wenn nicht gewonnen) */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-2">
          <h2 className="text-[12px] uppercase tracking-widest text-[#00E575] font-bold mb-2">{a.won ? "Vollständige Kundendaten" : <><EyeOff size={11} className="inline mr-1"/> Lead-Vorschau (anonymisiert)</>}</h2>
          {a.won && a.lead_full ? (
            <div className="text-[13px] space-y-1">
              <div><b>{a.lead_full.salutation} {a.lead_full.first_name} {a.lead_full.last_name}</b></div>
              <div className="text-zinc-300">{a.lead_full.email} · {a.lead_full.phone}</div>
              <div className="text-zinc-400">{a.lead_full.street}, {a.lead_full.zip} {a.lead_full.city}</div>
              <div className="mt-2 text-zinc-400">Heizung: {a.lead_full.current_heating} · {a.lead_full.living_area_sqm} m² · {a.lead_full.occupants} Personen</div>
              <div className="text-zinc-400">Termin: {a.lead_full.appointment_type} am {a.lead_full.appointment_date} um {a.lead_full.appointment_time}</div>
              {a.lead_full.agent_note && <div className="text-zinc-400">Notiz: <i>{a.lead_full.agent_note}</i></div>}
            </div>
          ) : (
            <div className="text-[13px] space-y-1">
              <div className="text-zinc-300"><MapPin size={12} className="inline mr-1"/> {lp.zip} {lp.city}</div>
              <div className="text-zinc-400"><Flame size={12} className="inline mr-1"/> {lp.current_heating} · {lp.living_area_sqm || "?"} m²</div>
              <div className="text-zinc-400">Gebäude: {lp.building_type || "—"}</div>
              <div className="text-zinc-400">Termin: {lp.appointment_type} am {lp.appointment_date} um {lp.appointment_time}</div>
              <div className="mt-2 text-[12px] text-zinc-300 italic inline-flex items-center gap-1"><ShieldCheck size={11}/> Kundenkontakt erst nach Gewinn freigeschaltet</div>
            </div>
          )}
        </section>

        {/* Bidding Box */}
        {!a.won && (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
            <div className="grid grid-cols-2 gap-3 mb-4 text-[12px] font-medium">
              <div><div className="text-[12px] uppercase text-zinc-300">Mindestgebot</div><div className="text-zinc-100 font-bold">{eur(a.min_bid_eur)}</div></div>
              <div><div className="text-[12px] uppercase text-zinc-300">Restzeit</div><div className={`font-bold ${remain < 600 ? "text-amber-400" : "text-zinc-100"}`}>{formatRemain(remain)}</div></div>
            </div>
            {a.my_bid ? (
              <div className="bg-[#00E575]/5 border border-[#00E575]/40 rounded-xl p-4 text-center" data-testid="auction-my-bid">
                <div className="text-[12px] uppercase tracking-widest text-[#00E575] mb-1">Dein Gebot</div>
                <div className="text-2xl font-bold text-[#00E575]">{eur(a.my_bid.amount_eur)}</div>
                <div className="text-[12px] text-zinc-300 mt-1">platziert {new Date(a.my_bid.placed_at).toLocaleString("de-DE")}</div>
                <div className="text-[12px] text-zinc-400 mt-3 inline-flex items-center gap-1"><EyeOff size={11}/> Verdeckt — keine Änderung möglich</div>
              </div>
            ) : expired ? (
              <div className="bg-zinc-800/50 rounded-xl p-4 text-center text-zinc-400 text-[13px] inline-flex items-center gap-2 w-full justify-center"><AlertTriangle size={14}/> Diese Auktion ist nicht mehr offen</div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Dein Gebot in €</label>
                  <input type="number" min={a.min_bid_eur} step="1" value={amount} onChange={e => setAmount(e.target.value)} data-testid="auction-bid-amount" placeholder={`min ${a.min_bid_eur}`} className="h-12 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-lg text-zinc-50 focus:border-[#00E575] outline-none"/>
                </div>
                <button onClick={submit} disabled={submitting || !amount} data-testid="auction-bid-submit"
                  className="w-full h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00C964] disabled:bg-zinc-800 disabled:text-zinc-500">
                  {submitting ? <Loader2 size={16} className="animate-spin"/> : <Gavel size={16}/>} Gebot verbindlich abgeben
                </button>
                <p className="text-[12px] text-zinc-300 leading-relaxed inline-flex items-start gap-1.5"><EyeOff size={12} className="mt-0.5 shrink-0"/> Verdeckte Auktion — du siehst die Gebote der anderen nicht. Höchstes Gebot gewinnt. 1 Gebot pro Auktion.</p>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
